import React, { useState } from 'react';

import type { AttainmentDrillNode, AttainmentPeriodMeta } from '../../../hooks/attainment-hooks';
import { attainmentPct, formatUsdCompact } from '../../../support/enablementLeader';
import {
  findPeriodByLabel,
  quotaBookingsRows,
  reportAttainmentRows,
  reportBookingsRows,
  teamDataState,
} from '../../../support/attainmentLeader';
import StbAccordion from '../../security-toolbox-ui/StbAccordion';
import CampaignChartCard, { ChartGrid, KpiCards } from '../Campaigns/CampaignChartCard';
import AttainmentElementsSection from './AttainmentElementsSection';
import AttainmentNoData from './AttainmentNoData';
import AttainmentTeamMembersTable from './AttainmentTeamMembersTable';

/**
 * Leader > Attainment > Team. Headline figures and charts for the selected leader's team plus a
 * per-report breakdown, all read from each person's own `periods[]` entry for the selected interval in the
 * `goal/attainment/drill` node (never its `rollup`, which double-counts a manager's subtree). The
 * reporting structure here comes from the drill response, not the User Access org chart.
 */

interface Props {
  node: AttainmentDrillNode;
  /** The goaling interval (e.g. H1 FY27) all figures refer to. */
  interval: AttainmentPeriodMeta | null;
  onSelectPerson: (email: string) => void;
}

const HBAR_SETTINGS = {
  valueFormat: 'number',
  axisLeftLegendOffset: -96,
  marginLeft: 112,
  maxLeftMarginRatio: 0.5,
};

export default function AttainmentTeamSummaryPage({ node, interval, onSelectPerson }: Props) {
  const [isTeamOpen, setTeamOpen] = useState(true);
  const owner = node.email.toLowerCase();
  const intervalLabel = interval?.label || interval?.interval || '';
  const state = teamDataState(node, intervalLabel);

  // Nothing to show for this interval: no KPIs, charts or rows — never another interval's numbers.
  if (state === 'none') return <AttainmentNoData intervalLabel={intervalLabel} who={owner} />;

  // The team's figures are the leader's own period for this interval: a manager's plan already covers
  // their team, so nothing is summed over the subtree.
  const period = findPeriodByLabel(node.own?.periods, intervalLabel);
  const stats = period?.total ?? null;
  const pct = attainmentPct(stats);
  const planElements = period?.elements ?? [];
  const reportBookings = reportBookingsRows(node, intervalLabel);
  const teamQuota = quotaBookingsRows(stats);

  return (
    <div className="flex flex-col gap-4 w-full min-w-0">
      <StbAccordion
          variant="minimal"
          label={intervalLabel ? `Team Attainment — ${intervalLabel}` : 'Team Attainment'}
          isOpen={true}
          toggleIsOpen={() => {}}
        >
          <div style={{ padding: 12 }}>
            <KpiCards
              kpis={[
                { label: 'Team attainment', value: pct == null ? '—' : `${pct}%` },
                { label: 'Team bookings', value: formatUsdCompact(stats?.bookings) },
                { label: 'Team quota', value: formatUsdCompact(stats?.quota) },
              ]}
            />
            <ChartGrid>
              <CampaignChartCard
                title="Attainment by report"
                subtitle="% of quota"
                chartType="horizontal_bar_chart"
                data={reportAttainmentRows(node, intervalLabel)}
                tileKey={`att-team-${owner}-pct`}
                tooltipKeys={{ label: 'Report', value: 'Attainment %' }}
                settings={{ ...HBAR_SETTINGS, axisBottomLegend: 'Attainment %', axisLeftLegend: 'Report' }}
                empty="No attainment recorded for these reports."
              />
              <CampaignChartCard
                title="Bookings by report"
                subtitle={`USD, ${reportBookings.scale.unit}`}
                chartType="horizontal_bar_chart"
                data={reportBookings.rows}
                tileKey={`att-team-${owner}-bookings-${reportBookings.scale.unit}`}
                tooltipKeys={{ label: 'Report', value: `USD (${reportBookings.scale.unit})` }}
                settings={{
                  ...HBAR_SETTINGS,
                  axisBottomLegend: `USD (${reportBookings.scale.unit})`,
                  axisLeftLegend: 'Report',
                }}
                empty="No bookings recorded for these reports."
              />
              <CampaignChartCard
                title="Team quota vs bookings"
                subtitle={`USD, ${teamQuota.scale.unit}`}
                chartType="horizontal_bar_chart"
                data={teamQuota.rows}
                tileKey={`att-team-${owner}-quota-${teamQuota.scale.unit}`}
                tooltipKeys={{ label: 'Measure', value: `USD (${teamQuota.scale.unit})` }}
                settings={{ ...HBAR_SETTINGS, axisBottomLegend: `USD (${teamQuota.scale.unit})` }}
                empty="No quota recorded for this team."
              />
            </ChartGrid>
          </div>
        </StbAccordion>

      {planElements.length > 0 && (
        <StbAccordion
          variant="minimal"
          label={`Team Plan Elements${intervalLabel ? ` — ${intervalLabel}` : ''}`}
          isOpen={true}
          toggleIsOpen={() => {}}
        >
          <div style={{ padding: 12 }}>
            <AttainmentElementsSection
              elements={planElements}
              keyPrefix={`att-team-${owner}`}
              intervalLabel={intervalLabel}
            />
          </div>
        </StbAccordion>
      )}

      <StbAccordion
        variant="minimal"
        label={intervalLabel ? `By Team Member — ${intervalLabel}` : 'By Team Member'}
        isOpen={isTeamOpen}
        toggleIsOpen={setTeamOpen}
      >
        <div style={{ minHeight: 120, padding: 12 }}>
          <AttainmentTeamMembersTable
            node={node}
            intervalLabel={intervalLabel}
            onSelectEmail={onSelectPerson}
          />
        </div>
      </StbAccordion>
    </div>
  );
}
